import { useContext, useMemo } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { TableEntrySource } from '@/analytics/enums/table.enums'
import { trackTableOpened } from '@/analytics/utils/table-analytics'
import { AuthContext } from '@/context/auth-context'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { MatterActivityEmptyState } from '@/matter/components/matter-overview/MatterActivityEmptyState'
import { buildMemberNamesById } from '@/matter/components/matter-overview/conversation-owner-display'
import { useNavigateToDraftInChat } from '@/drafting/hooks/useNavigateToDraftInChat'
import { DraftEntryPoint } from '@/analytics/enums/draft.enums'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { WhereYouLeftOffActivityItem } from '@/matter/where-you-left-off/components/WhereYouLeftOffActivityItem'
import { useCreateNewChat } from '@/chat-common/new_chat/hooks/useCreateNewChat'
import { useAppSelector } from '@/store/store-hooks'
import { selectMatterMembers, selectMatterMembersInitialLoadComplete } from '@/matter/store/selectors'
import { kUnavailableMemberDisplayName } from '@/collaboration/utils/members'
import { matterPaths, kRouteSegments } from '@/routing/paths'
import type { MatterRead } from '@/matter/store/schemas'
import { useActivityFeed } from '../hooks/useActivityFeed'
import { activityRowKey } from '../utils/activity-row-key'
import { ActivityEventType, MatterRecordType, isDraftRow, type ActivityFeedRow } from '../schemas'
import { ActivityFeedListItem } from './ActivityFeedListItem'
import { ActivityFeedSkeleton } from './ActivityFeedSkeleton'
import { Retry } from '@/assets/icons'

const recordPaths: Record<MatterRecordType, (matterId: string) => string> = {
  [MatterRecordType.MedChron]: matterPaths.chronology.index,
  [MatterRecordType.MedSummary]: matterPaths.billing.index,
  [MatterRecordType.Timeline]: matterPaths.timeline.index,
}

/** The matter overview's activity feed: one row per chat, draft, and table in the matter, plus its record activity. */
export function ActivityFeed({ matter }: { matter: MatterRead }) {
  const matterId = matter.id
  const { rows, isLoadingWithoutCachedRows, isError, canRevealMore, isEndOfActivity, revealMore, refetch } = useActivityFeed(matterId)
  const members = useAppSelector((state) => selectMatterMembers(state, matterId))
  const membersLoaded = useAppSelector((state) => selectMatterMembersInitialLoadComplete(state, matterId))
  const memberNamesById = useMemo(() => buildMemberNamesById(members), [members])
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const navigateToDraft = useNavigateToDraftInChat()
  const { createNewChat } = useCreateNewChat()

  // Nobody to attribute a row to in a matter of one, where rows read as the bare activity instead, for example "Chat updated".
  const isSoloMatter = matter.member_count <= 1

  function resolveActorLabel(actorUserId: string) {
    if (isSoloMatter) return undefined
    if (actorUserId === user?.uid) return 'You'
    // A member lookup that hasn't loaded yet is not a member who has left.
    return memberNamesById.get(actorUserId) ?? (membersLoaded ? kUnavailableMemberDisplayName : undefined)
  }

  function openRow(row: ActivityFeedRow) {
    switch (row.event) {
      case ActivityEventType.ChatUpdated:
        navigate(buildConversationPath(matterId, row.conversation_id, kRouteSegments.agentV2))
        return
      case ActivityEventType.DraftGenerated:
      case ActivityEventType.DraftUpdated:
        navigateToDraft({
          artifactVersionId: row.artifact_version_id,
          matterArtifactId: row.artifact_id,
          conversationId: row.conversation_id,
          draftEntryPoint: DraftEntryPoint.MatterActivity,
        })
        return
      case ActivityEventType.TableCreated:
      case ActivityEventType.TableUpdated:
        trackTableOpened(trackEvent, { tableId: row.table_id, matterId, tableEntrySource: TableEntrySource.MatterOverview })
        navigate(matterPaths.table(matterId, row.table_id))
        return
      case ActivityEventType.RecordGenerated:
      case ActivityEventType.RecordUpdated:
        navigate(recordPaths[row.record_type](matterId))
    }
  }

  return (
    <section className="w-full min-w-0" aria-label="Matter activity">
      <div className="sticky top-0 z-[9] flex h-8 border-b border-brand-neutral-200 bg-brand-neutral-50">
        <h2 className="flex items-center border-b-2 border-brand-800 pt-px font-rethink-sans text-sm font-medium text-brand-800">Matter Activity</h2>
      </div>
      <div>
        <WhereYouLeftOffActivityItem matterId={matterId} />
        {/* A refresh that fails leaves the rows it already had on screen, with the only way to ask again. */}
        {isError && rows.length > 0 && (
          <InlineMessage variant="neutral">
            <span className="flex w-full items-center gap-2">
              Could not refresh activity.
              <Button variant="tonal" color="secondary" size="xs" className="ml-auto" leadingIcon={<Retry className="size-3" />} onClick={refetch}>
                Retry
              </Button>
            </span>
          </InlineMessage>
        )}
        {isLoadingWithoutCachedRows ? (
          <div role="status" aria-label="Loading matter activity">
            {Array.from({ length: 4 }, (_, index) => (
              <ActivityFeedSkeleton key={index} index={index} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="py-4">
            <MatterActivityEmptyState
              showError={isError}
              onRetry={refetch}
              errorMessage="There was an error loading your activity"
              emptyMessage="No activity in this matter yet"
              actionLabel="Start a new chat"
              onAction={() => createNewChat(true, matterId)}
            />
          </div>
        ) : (
          <ul aria-label="Chats, drafts, tables and matter records">
            {rows.map((row) => (
              <ActivityFeedListItem
                key={activityRowKey(row)}
                row={row}
                actorLabel={resolveActorLabel(row.actor_user_id)}
                conversationPath={isDraftRow(row) ? buildConversationPath(matterId, row.conversation_id, kRouteSegments.agentV2) : undefined}
                onOpen={() => openRow(row)}
              />
            ))}
          </ul>
        )}
        {canRevealMore && (
          <div className="flex justify-center py-4">
            <Button variant="plain" color="neutral" size="sm" onClick={revealMore}>
              Load more
            </Button>
          </div>
        )}
        {/* Rendered whether or not it has anything to say: a live region only announces changes made
            after it is already in the accessibility tree. */}
        <p aria-live="polite" className={clsx('text-center text-xs text-brand-neutral-800', isEndOfActivity && 'py-4')}>
          {isEndOfActivity ? "That's all the activity in this matter" : null}
        </p>
      </div>
    </section>
  )
}
